'use client';
import useProducts from '@hooks/product/useProducts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RightArrow, LeftArrow } from '@repo/ui/src/components/Carousels/CarouselArrows';
import { logProduct } from '@utils/productWrapper';
import { useIsSM, useIsMD, useIsLG, useIsXL, useIsMobile } from '@repo/ui/src/utils/useMediaQuery';
import { useIntersectionObserver } from '@repo/ui/src/hooks/useIntersectionObserver';

import useEmblaCarousel from 'embla-carousel-react';
import AnimatedSlider from './_AnimatedSlider';
import CarouselItem from './_CarouselItem';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import SlideWrapper from './_SlideWrapper';
import { useVipAccess } from '@hooks/customer';

import type { CarouselResolvedItem, GeneralCarouselClientProps } from './types';
import type { ProductModelProps } from '@typings/product';

export type { GeneralCarouselClientProps } from './types';

const GeneralCarouselClient = ({
  title,
  description,
  viewAllLink,
  body,
  _data = body,
  _testID,
  _meta,
  headerLeft,
  maxVisibleCards = 3.5,
  darkMode,
  linkAsButton,
  whiteBackground,
  saleText,
  newText,
  noPadding,
  className,
}: GeneralCarouselClientProps) => {
  // Memoize the filtered list so its reference is stable across re-renders.
  // Without this, the inline .filter() creates a new array every render, causing
  // useProducts' internal useMemo to recompute → new `products` reference → double DL push.
  const productItems = useMemo(
    () =>
      _data?.filter(
        (item): item is ProductModelProps =>
          item?._meta?.schema === 'https://traegergrills.com/data/contentTypes/product.json',
      ),
    [_data],
  );
  const { products, isLoading: isProductsLoading } = useProducts(productItems);
  const { vip } = useVipAccess();
  const items: Array<CarouselResolvedItem> | undefined = products?.length
    ? (products as unknown as Array<CarouselResolvedItem>)
    : _data;
  const isMobile = useIsMobile();
  const isSmall = useIsSM();
  const isMedium = useIsMD();
  const isLarge = useIsLG();
  const isXL = useIsXL();
  const { locale } = useCurrentUrl();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [mediaReady, setMediaReady] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ watchDrag: isMobile, align: 'start' });

  useEffect(() => {
    setMediaReady(true);
  }, []);

  const entry = useIntersectionObserver(carouselRef, {
    threshold: 0.1,
    freezeOnceVisible: true,
  });

  // Only fire product list view when carousel is in view and enriched product data (including shop data) is ready.
  // Guarding on `isProductsLoading` and `products` (not `items`) prevents a double-fire:
  // `items` transitions from raw `_data` → enriched `products`, which previously triggered two pushes.
  useEffect(() => {
    if (isProductsLoading || !products.length || !entry?.isIntersecting) {
      return;
    }

    logProduct('PRODUCT_LIST_VIEWED', {
      product: products,
      locale,
      list_name: title,
      list_id: _meta?.deliveryId,
    });
  }, [entry?.isIntersecting, _meta?.deliveryId, products, isProductsLoading, locale, title]);

  const slideCount = useMemo(() => {
    if (!items?.length) {
      return 1;
    }
    if (items?.length === 4 && isXL) {
      return Math.min(4, maxVisibleCards);
    }
    if (items?.length <= 3 && isLarge) {
      return Math.min(items.length, maxVisibleCards);
    }
    if (items?.length <= 2 && isMedium) {
      return Math.min(items.length, maxVisibleCards);
    }
    if (!isSmall) {
      return Math.min(1, maxVisibleCards);
    }
    if (!isMedium) {
      return Math.min(1.5, maxVisibleCards);
    }
    if (!isLarge) {
      return Math.min(headerLeft ? 1.5 : 2.5, maxVisibleCards);
    }
    if (!isXL) {
      return Math.min(headerLeft ? 2.5 : 3.5, maxVisibleCards);
    }
    return maxVisibleCards;
  }, [isSmall, isMedium, isLarge, isXL, headerLeft, items?.length, maxVisibleCards]);

  const totalSlides = items?.length ?? 0;
  const hideSlider = totalSlides === slideCount;
  const center = hideSlider && totalSlides < 5;

  // Re-initialize embla when slideCount changes (responsive breakpoints change)
  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, slideCount]);

  // Track selected slide index
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!items) return null;

  return (
    <div
      ref={carouselRef}
      className={classNames(
        darkMode && 'bg-neutral-600',
        whiteBackground && 'bg-white',
        className,
        !darkMode && !whiteBackground && 'bg-warm-200',
      )}
    >
      <div
        role="region"
        aria-label={`${title} carousel`}
        data-test-id={`reference_carousel_${_testID}`}
        className={classNames(
          'py-8 md:py-20 max-w-[1680px] m-auto',
          headerLeft && 'flex flex-col md:flex-row',
          hideSlider && 'pr-10 xl:pr-0',
          !noPadding && 'pl-4 sm:pl-9 md:pl-20',
          !noPadding && products?.length > 0 && !hideSlider && 'pr-4',
        )}
      >
        <p className="sr-only">This is a carousel. Tab through the slides for content.</p>
        {title && (
          <div
            className={classNames(
              'flex justify-between pb-8 gap-x-8 pr-5 sm:pb-10 flex-col sm:flex-row md:pr-10',
              headerLeft &&
                'flex sm:flex-col sm:pb-8 justify-start sm:justify-start md:justify-start md:w-1/4 md:pb-0',
            )}
          >
            {title && (
              <div>
                <h2
                  className={classNames(
                    'tra-text-web-heading-2 mb-6',
                    darkMode && 'text-white',
                  )}
                >
                  {title}
                </h2>
                {description && (
                  <p className={classNames('pr-10 mb-0', darkMode && 'text-white')}>
                    {description}
                  </p>
                )}
                <hr
                  className={classNames(
                    'border-2 w-10 border-brand-300 my-3 sm:mt-6 sm:mb-0',
                    headerLeft && 'sm:mt-4 sm:mb-4',
                  )}
                />
              </div>
            )}
            <div
              className={classNames(
                'md:w-6/12 flex',
                headerLeft ? 'md:w-full md:pr-0' : 'md:pr-20 sm:justify-end',
              )}
            >
              {items && items?.length > 2 && viewAllLink?.link && (
                <div className={classNames(!description && 'pt-10 md:pt-2')}>
                  <Button
                    variant={linkAsButton ? ButtonVariant.primary : ButtonVariant.knife}
                    as={Anchor}
                    {...viewAllLink}
                    className={classNames('h-auto', darkMode && 'text-white')}
                  >
                    {viewAllLink?.displayName}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {mediaReady && (
          <div className={classNames(headerLeft && 'md:w-3/4 flex flex-col')}>
            <div ref={emblaRef} data-testid="gc-carousel" className="overflow-hidden">
              <div
                className={classNames(
                  'flex',
                  center && 'justify-center',
                  products?.length > 0 && 'border-l border-neutral-200',
                )}
              >
                {items?.filter(Boolean).map((item, i) => (
                  <SlideWrapper
                    key={item._meta?.deliveryId ?? i}
                    index={i}
                    className={classNames(
                      products?.length > 0 ? 'border-t border-neutral-200' : 'pr-6',
                    )}
                    length={items?.length}
                    slideCount={slideCount}
                    selectedIndex={selectedIndex}
                    emblaApi={emblaApi}
                  >
                    <CarouselItem
                      item={item}
                      saleText={saleText}
                      newText={newText}
                      darkMode={darkMode}
                      vip={vip}
                    />
                  </SlideWrapper>
                ))}
              </div>
            </div>
            {!hideSlider && (
              <div
                className="flex items-center justify-between w-full pt-10 pr-5 md:pr-20"
                data-testid="gc-slider"
              >
                <div className="w-full mr-10">
                  <AnimatedSlider
                    darkMode={darkMode}
                    slideCount={slideCount}
                    totalSlides={totalSlides}
                    selectedIndex={selectedIndex}
                  />
                </div>
                <div className="flex justify-between w-28 gap-x-2">
                  <button
                    aria-label="previous"
                    className="bg-transparent border-0"
                    onClick={() => emblaApi?.scrollPrev()}
                  >
                    <LeftArrow aria-label={null} darkMode={darkMode} asSpan />
                  </button>
                  <button
                    aria-label="next"
                    className="bg-transparent border-0"
                    onClick={() => emblaApi?.scrollNext()}
                  >
                    <RightArrow aria-label={null} darkMode={darkMode} asSpan />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralCarouselClient;
